// ADDITIONAL DETAIL (owner, 2026-09-25 — D-154): *"The Marketing Category
// field does not justify its own row. Let's treat it like we treat Associated
// Entities."*
//
// The icon to the LEFT of a line's Associated Entities icon, and the pop-up it
// opens. It replaces the Marketing sub-row that used to sit under a qualifying
// line, in both tables:
//  - a MARKETING line (its account is a Marketing account) shows the
//    Marketing Category, which it owes;
//  - a line copied in from FLEET MAINTENANCE (D-381) shows that app's own
//    detail — Category, Action, Parts Cost, Labor Cost, Labor Hours and Notes —
//    as plain read-only values. Nobody here picks or edits it, and no line owes
//    it, so it is never red;
//  - a line that is both shows both.
// THE ICON SHOWS ON EVERY LINE, WHEREVER IT SITS (owner, 2026-09-28). On a line
// that has nothing it stays grey and the pop-up says so in one sentence. It
// lights up blue when the line owes a Marketing Category and has it, or carries
// Fleet Maintenance's detail, and wears the red border when a Marketing
// Category it owes is empty and the viewer can fill it in. Which sections a
// line gets, and when anything inside is red, come from `../lib/required.ts`.
// The icon and the pop-up frame are `LineItemPopover`, shared with Associated
// Entities.
//
// THE MARKETING CATEGORY IS EDITABLE AT IMPORT CONTROL (D-190; there since
// AP/AR Approval merged into it, D-216), and wherever the Account is (D-192).
// Anywhere else it is PLAIN TEXT. A hold changes nothing here: a held
// invoice's fields stay editable (owner, 2026-10-03 — D-282).
//
// The field does not save itself. The caller passes its own save wiring —
// the editor's detail debounce and mutex, or the read-only table's one-pick
// write — so this file owns the look and nothing about persistence.
import { Fragment, useEffect, useRef, useState } from 'react'
import { type AdditionalDetailSections } from '@ap/shared'
import { Caption1, makeStyles, tokens } from '@fluentui/react-components'
import { ClipboardTaskListLtrFilled, ClipboardTaskListLtrRegular } from '@fluentui/react-icons'
import { searchMarketingCategories } from '../lib/data'
import { transportationDetailRows } from '../lib/format'
import { type AdditionalDetailState } from '../lib/required'
import type { LineItemTransportationDetail } from '../lib/types'
import { useLineItemPanelStyles } from '../lib/theme'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { LineItemPopover } from './LineItemIconButton'

/** The name on the icon and the heading in the pop-up. */
export const ADDITIONAL_DETAIL_LABEL = 'Additional Detail'

/** What the pop-up says on a line that has nothing. */
export const NOTHING_OWED_NOTE =
  'Nothing else is needed for this line. A Marketing Category is needed on a line coded to a Marketing Account.'

const useStyles = makeStyles({
  // Label, then value, one pair per row, labels in one column.
  grid: {
    display: 'grid',
    gridTemplateColumns: '170px minmax(0, 1fr)',
    alignItems: 'center',
    columnGap: '10px',
    rowGap: '6px',
  },
  label: { fontSize: '12px', color: tokens.colorNeutralForeground2, whiteSpace: 'nowrap' },
  text: { fontSize: tokens.fontSizeBase300, minWidth: 0, overflowWrap: 'anywhere' },
})

/** The Marketing Category section. */
interface CategorySection {
  /** The picked category's id, and its "code — context" text (`detailCategoryLabel`). */
  categoryId: string | null
  label: string
  /** Present = editable here. Absent = plain text. */
  onSelect?: (option: EntityOption) => void
  /** A write in flight for this line. */
  saving?: boolean
  /** Remount key, so a refused pick re-seeds from the stored value. */
  pickerKey?: string
  /** The refusal message's id while this category's write is refused (C1-09). */
  describedBy?: string
}

interface Props {
  sections: AdditionalDetailSections
  state: AdditionalDetailState
  marketing?: CategorySection
  /** Fleet Maintenance's detail for the line, shown read-only; `null` when it has none. */
  transportation?: LineItemTransportationDetail | null
  /** A sentence in place of the fields — an unsaved line has nothing to write to yet. */
  hint?: string
  mountNode?: HTMLElement | null
  /** The icon shows but does not open — the line-item bulk row's Apply mode,
      which writes no Additional Detail (D-242). */
  disabled?: boolean
}

export function AdditionalDetailPopover({ sections, state, marketing, transportation, hint, mountNode, disabled }: Props) {
  return (
    <LineItemPopover
      regularIcon={ClipboardTaskListLtrRegular}
      filledIcon={ClipboardTaskListLtrFilled}
      label={ADDITIONAL_DETAIL_LABEL}
      filled={state.filled}
      requiredMissing={state.requiredMissing}
      mountNode={mountNode}
      disabled={disabled}
    >
      <AdditionalDetailPanel sections={sections} state={state} marketing={marketing} transportation={transportation} hint={hint} />
    </LineItemPopover>
  )
}

function AdditionalDetailPanel({ sections, state, marketing, transportation, hint }: Omit<Props, 'mountNode' | 'disabled'>) {
  const ps = useLineItemPanelStyles()
  const s = useStyles()
  // Listboxes mount INSIDE the pop-up, so picking an option is not an outside
  // click that dismisses it — the Associated Entities editor's own fix.
  const rootRef = useRef<HTMLDivElement>(null)
  const [popupHost, setPopupHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setPopupHost(rootRef.current)
  }, [])

  const detail = transportation ?? null
  const owed = (sections.marketing && !!marketing) || detail !== null
  const live = !hint

  return (
    <div className={ps.root} ref={rootRef}>
      <div className={ps.head}>
        <span className={ps.headLabel}>{ADDITIONAL_DETAIL_LABEL}</span>
      </div>
      {!owed && <Caption1 className={ps.note}>{NOTHING_OWED_NOTE}</Caption1>}
      {owed && hint && <Caption1 className={ps.note}>{hint}</Caption1>}
      {owed && !hint && (
        <div className={s.grid}>
          {sections.marketing && marketing && (
            <>
              <span className={s.label}>Marketing Category</span>
              {live && marketing.onSelect !== undefined ? (
                <EntityPicker
                  key={marketing.pickerKey}
                  size="small"
                  ariaLabel="Marketing Category"
                  mountNode={popupHost}
                  invalid={state.marketingInvalid}
                  // Busy, not disabled, while it saves: focus stays put (C2-06).
                  busy={marketing.saving}
                  describedBy={marketing.describedBy}
                  value={{ id: marketing.categoryId ?? undefined, name: marketing.categoryId ? marketing.label : undefined }}
                  search={(t) =>
                    searchMarketingCategories(t).then<EntityOption[]>((cs) =>
                      cs.map((c) => ({ id: c.id, primary: c.category, secondary: c.context })),
                    )
                  }
                  onSelect={(o) => marketing.onSelect?.(o)}
                />
              ) : (
                <span className={s.text}>{marketing.label}</span>
              )}
            </>
          )}
          {/* Fleet Maintenance's detail: plain read-only values, never a control (D-381). */}
          {detail !== null &&
            transportationDetailRows(detail).map((r) => (
              <Fragment key={r.label}>
                <span className={s.label}>{r.label}</span>
                <span className={s.text}>{r.value}</span>
              </Fragment>
            ))}
        </div>
      )}
    </div>
  )
}
